import { getWidthForArrayOfStrings } from '@thoughtspot/custom-style-service';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import _ from 'lodash';
import { isHighchartsProvider, isXAxisDate } from '../core/chart-model-utils';
import { getXAxisLabelMaxWidth, isXAxisVertical, } from '../core/chart-type-specification-service';
import { getResponsiveBreakpoint, ScreenSizeClass, } from '../core/scaling-service';
import { getSeries, getTotalRowCount } from '../data/data-util';
import { ChartThemeService } from '../theme/chart-theme-service';
const DEFAULT_AUTO_ROTATION_LIMIT = 70;
export const DEFAULT_ANGULAR_ROTATION_IN_DEG = -45;
export function castAxisConfig(config, axisDetails) {
    const xAxisDetails = axisDetails.x;
    const xAxisConfig = config.config.xAxis;
    if (xAxisConfig && Array.isArray(xAxisDetails)) {
        config.config.xAxis = xAxisDetails.map(() => xAxisConfig);
    }
    const yAxisDetails = axisDetails.y;
    const yAxisConfig = config.config.yAxis;
    if (yAxisConfig && Array.isArray(yAxisDetails)) {
        config.config.yAxis = yAxisDetails.map(() => yAxisConfig);
    }
    return config;
}
export function isScrollApplied(xAxisConfig, dataModel) {
    var _a, _b, _c;
    const scrollBarEnabled = ((_a = xAxisConfig.scrollbar) === null || _a === void 0 ? void 0 : _a.enabled) ||
        ((_c = (_b = xAxisConfig.userOptions) === null || _b === void 0 ? void 0 : _b.scrollbar) === null || _c === void 0 ? void 0 : _c.enabled) ||
        false;
    if (!scrollBarEnabled)
        return false;
    const series = getSeries(dataModel);
    let minExtreme = Number.POSITIVE_INFINITY;
    let maxExtreme = Number.NEGATIVE_INFINITY;
    series.forEach(serie => {
        const data = serie.data;
        if (data.length > 0) {
            minExtreme = minExtreme
                ? Math.min(minExtreme, _.minBy(data, 'x').x)
                : _.minBy(data, 'x').x;
            maxExtreme = maxExtreme
                ? Math.max(maxExtreme, _.maxBy(data, 'x').x)
                : _.maxBy(data, 'x').x;
        }
    });
    if (minExtreme < xAxisConfig.min || maxExtreme > xAxisConfig.max) {
        return true;
    }
    return false;
}
export function getEffectiveFontSizeForLegend(vizModel, chartConfig, shouldScale) {
    let fontSize;
    if (isHighchartsProvider(vizModel)) {
        return chartConfig === null || chartConfig === void 0 ? void 0 : chartConfig.config.legend.itemStyle.fontSize;
    }
    let screenSizeClass = ScreenSizeClass.HD;
    if (shouldScale) {
        const currentBreakpoint = getResponsiveBreakpoint();
        screenSizeClass = currentBreakpoint.screenSizeClass;
    }
    if (screenSizeClass === ScreenSizeClass.HD) {
        fontSize = '12px';
    }
    else if (screenSizeClass === ScreenSizeClass.FHD) {
        fontSize = '20px';
    }
    else if (screenSizeClass === ScreenSizeClass.QHD) {
        fontSize = '24px';
    }
    else if (screenSizeClass === ScreenSizeClass.UHD) {
        fontSize = '48px';
    }
    return fontSize;
}
export function getScreenSizeClass(shouldScale = true) {
    let screenSizeClass = ScreenSizeClass.HD;
    if (shouldScale) {
        const currentBreakpoint = getResponsiveBreakpoint();
        screenSizeClass = currentBreakpoint.screenSizeClass;
    }
    return screenSizeClass;
}
export function getFontByScreenSize(screenSizeClass) {
    switch (screenSizeClass) {
        case ScreenSizeClass.HD:
            return '12px';
        case ScreenSizeClass.FHD:
            return '20px';
        case ScreenSizeClass.QHD:
            return '24px';
        case ScreenSizeClass.UHD:
            return '48px';
        default:
            return '12px';
    }
}
function shouldRotateLabelsBecauseOfScroll(xAxisConfig, chartModel, dataModel) {
    const shouldSkipRotation = isXAxisVertical(chartModel.config.chartType) || isXAxisDate(chartModel);
    return isScrollApplied(xAxisConfig, dataModel) && !shouldSkipRotation;
}
function shouldRotateLabelsBecauseOfSpace(chartModel, dataModel, width, fontSize, padding) {
    const shouldSkipRotation = isXAxisVertical(chartModel.config.chartType) || isXAxisDate(chartModel);
    if (shouldSkipRotation) {
        return false;
    }
    const fontDetails = ChartThemeService.getDefaultTheme().xAxis.labels.style;
    const font = `${fontSize} ${fontDetails.fontFamily}`;
    const dataArray = _.map(dataModel.xValueToRawValues, xValueToRawValue => xValueToRawValue.map(item => item.value).join(', '));
    return getWidthForArrayOfStrings(dataArray, font, padding) > width;
}
function validateRowCount(chartModel) {
    return (getTotalRowCount(chartModel) >=
        envFlags.getValue(FLAGS.maxLabelCount.name));
}
function getChartScalingProps(width, xGridLineEnabled, yGridLineEnabled, axisDetails, shouldScale, chartModel, dataModel, enablePiechartAnimation, height) {
    let screenSizeClass = ScreenSizeClass.HD;
    if (shouldScale) {
        const currentBreakpoint = getResponsiveBreakpoint();
        screenSizeClass = currentBreakpoint.screenSizeClass;
    }
    let config = {};
    const fontSize = getFontByScreenSize(screenSizeClass);
    let lineWidth;
    let shouldRotateAxisLabels = false;
    const shouldRotateAxisLabelsBecauseOfScroll = shouldRotateLabelsBecauseOfScroll(axisDetails.x, chartModel, dataModel);
    if (screenSizeClass === ScreenSizeClass.HD) {
        lineWidth = 2;
        config = {
            meta: {
                legendEnabled: true,
            },
            config: {
                spacingTop: 15,
                spacingLeft: 3,
                spacingRight: 3,
                spacingBottom: 0,
                tooltip: {
                    style: {
                        padding: '15px',
                    },
                },
                xAxis: {
                    labels: {
                        autoRotationLimit: DEFAULT_AUTO_ROTATION_LIMIT,
                        padding: 15,
                        rotation: undefined,
                    },
                    title: {
                        margin: 10,
                    },
                },
                yAxis: {
                    minPadding: 0,
                    tickPixelInterval: 100,
                },
            },
        };
        if (shouldRotateAxisLabelsBecauseOfScroll) {
            config.config.xAxis.labels.rotation = DEFAULT_ANGULAR_ROTATION_IN_DEG;
        }
    }
    else if (screenSizeClass === ScreenSizeClass.FHD) {
        lineWidth = 2.4;
        config = {
            meta: {
                legendEnabled: false,
            },
            config: {
                spacingTop: 20,
                spacingLeft: 4,
                spacingRight: 4,
                spacingBottom: 0,
                tooltip: {
                    style: {
                        padding: '20px',
                    },
                },
                xAxis: {
                    labels: {
                        y: 28,
                        autoRotationLimit: DEFAULT_AUTO_ROTATION_LIMIT,
                        padding: 20,
                        rotation: undefined,
                    },
                    title: {
                        margin: 28,
                    },
                },
                yAxis: {
                    padding: 5,
                    tickPixelInterval: 135,
                },
            },
        };
    }
    else if (screenSizeClass === ScreenSizeClass.QHD) {
        lineWidth = 4;
        config = {
            meta: {
                legendEnabled: false,
            },
            config: {
                spacingTop: 30,
                spacingLeft: 6,
                spacingRight: 6,
                spacingBottom: 0,
                tooltip: {
                    style: {
                        padding: '30px',
                    },
                },
                xAxis: {
                    labels: {
                        y: 40,
                        autoRotationLimit: DEFAULT_AUTO_ROTATION_LIMIT,
                        padding: 30,
                        rotation: undefined,
                    },
                    title: {
                        margin: 40,
                    },
                },
                yAxis: {
                    padding: 10,
                    tickPixelInterval: 200,
                },
            },
        };
        if (shouldRotateAxisLabelsBecauseOfScroll) {
            config.config.xAxis.labels.rotation = DEFAULT_ANGULAR_ROTATION_IN_DEG;
        }
    }
    else if (screenSizeClass === ScreenSizeClass.UHD) {
        lineWidth = 8;
        config = {
            meta: {
                legendEnabled: false,
            },
            config: {
                spacingTop: 60,
                spacingLeft: 12,
                spacingRight: 12,
                spacingBottom: 0,
                tooltip: {
                    style: {
                        padding: '60px',
                    },
                },
                xAxis: {
                    labels: {
                        y: 80,
                        padding: 60,
                        rotation: undefined,
                        step: undefined,
                    },
                    title: {
                        margin: 80,
                    },
                },
                yAxis: {
                    padding: 20,
                    tickPixelInterval: 400,
                },
            },
        };
        shouldRotateAxisLabels = shouldRotateLabelsBecauseOfSpace(chartModel, dataModel, width, fontSize, 60);
        if (shouldRotateAxisLabels || shouldRotateAxisLabelsBecauseOfScroll) {
            config.config.xAxis.labels.rotation = DEFAULT_ANGULAR_ROTATION_IN_DEG;
        }
        else {
            config.config.xAxis.labels.autoRotationLimit = DEFAULT_AUTO_ROTATION_LIMIT;
        }
    }
    _.merge(config, {
        config: {
            borderWidth: lineWidth,
            plotOptions: {
                series: {
                    states: {
                        inactive: {
                            animation: {
                                duration: 250,
                            },
                        },
                    },
                    dataLabels: {
                        style: {
                            fontSize,
                        },
                    },
                },
                line: {
                    lineWidth,
                    states: {
                        inactive: {
                            enabled: !validateRowCount(chartModel),
                        },
                        hover: {
                            lineWidth: lineWidth * 2,
                        },
                    },
                },
                pie: {
                    animation: enablePiechartAnimation,
                    dataLabels: {
                        connectorWidth: lineWidth,
                    },
                },
                scatter: {
                    marker: {
                        radius: (5 * lineWidth) / 2,
                        lineWidth: lineWidth * 0.1,
                    },
                },
            },
            tooltip: {
                style: {
                    fontSize,
                },
            },
            legend: {
                title: {
                    style: {
                        fontSize,
                    },
                },
                itemStyle: {
                    fontSize,
                },
            },
            xAxis: {
                stackLabels: {
                    style: {
                        fontSize,
                    },
                },
                labels: {
                    style: {
                        fontSize,
                        width: shouldRotateAxisLabelsBecauseOfScroll
                            ? getXAxisLabelMaxWidth(height, isXAxisVertical(chartModel.config.chartType))
                            : undefined,
                        textOverflow: shouldRotateAxisLabelsBecauseOfScroll
                            ? 'ellipsis'
                            : undefined,
                    },
                },
                title: {
                    style: {
                        fontSize,
                    },
                },
            },
            yAxis: {
                stackLabels: {
                    style: {
                        fontSize,
                    },
                },
                labels: {
                    style: {
                        fontSize,
                    },
                },
                title: {
                    style: {
                        fontSize,
                    },
                },
            },
        },
    });
    return config;
}
export function getConfigForRotatedLabels(xAxisConfig, chartModel, dataModel) {
    const shouldRotateLabels = shouldRotateLabelsBecauseOfScroll(xAxisConfig, chartModel, dataModel);
    return {
        xAxis: {
            labels: {
                rotation: shouldRotateLabels
                    ? DEFAULT_ANGULAR_ROTATION_IN_DEG
                    : undefined,
            },
        },
    };
}
export function BuildHighchartStyleConfig(width, defaultScreenConfig, axisDetails, shouldScale, chartModel, dataModel, enablePiechartAnimation, height) {
    return getChartScalingProps(width, defaultScreenConfig.xGridlineEnabled, defaultScreenConfig.yGridlineEnabled, axisDetails, shouldScale, chartModel, dataModel, enablePiechartAnimation, height);
}
//# sourceMappingURL=highchart-theme-screen-util.js.map